import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Component from '../../components/Component';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { getPredictionCandidates, patchPredictionCandidates } from '../../services/predictionCandidatesApi';

const isSuccess = (res) => String(res?.statusCode || '') === '10000' || res?.statusCode == null;

const getPayload = (res) => {
  if (res?.data && typeof res.data === 'object' && !Array.isArray(res.data)) return res.data;
  if (res && typeof res === 'object' && !Array.isArray(res)) return res;
  return {};
};

const toNumberId = (value) => {
  const n = Number(value);
  if (Number.isNaN(n) || n <= 0) return null;
  return n;
};

const playerIdFromAny = (p) => toNumberId(p?.player_id ?? p?.id ?? p?.pid);

const playerNameFromAny = (p) => p?.name ?? p?.short_name ?? p?.player_name ?? null;

const getDisplayEntry = (displayById, pid) => {
  if (!displayById || pid == null) return null;
  return displayById[String(pid)] || displayById[Number(pid)] || null;
};

const playerLabel = (player, displayById) => {
  const pid = player.id;
  const display = getDisplayEntry(displayById, pid);
  const name = display?.name || display?.short_name || player.name || `Player ${pid}`;
  const shortName =
    display?.short_name && display?.name && String(display.short_name).trim() !== String(display.name).trim()
      ? ` (${display.short_name})`
      : '';
  return `${name}${shortName} [${pid}]`;
};

const getCandidateName = (pid, displayById, playersById) => {
  const display = getDisplayEntry(displayById, pid);
  if (display?.name) return display.name;
  if (display?.short_name) return display.short_name;
  return playersById.get(Number(pid))?.name || `Player ${pid}`;
};

function PredictionCandidates() {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialMid = searchParams.get('match_id') || '';

  const [matchIdInput, setMatchIdInput] = useState(initialMid);
  const [meta, setMeta] = useState(null);
  const [configs, setConfigs] = useState([]);
  const [availablePlayers, setAvailablePlayers] = useState([]);
  const [playerDisplayById, setPlayerDisplayById] = useState({});
  const [draftsByQuestionId, setDraftsByQuestionId] = useState({});
  const [loading, setLoading] = useState(false);
  const [savingQuestionId, setSavingQuestionId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const showSnackbar = useCallback((message, severity = 'success') => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const pickerPlayers = useMemo(() => {
    const byId = new Map();
    availablePlayers.forEach((p) => {
      const pid = playerIdFromAny(p);
      if (pid == null || byId.has(pid)) return;
      byId.set(pid, { id: pid, name: playerNameFromAny(p) });
    });
    return Array.from(byId.values()).sort((a, b) => a.id - b.id);
  }, [availablePlayers]);

  const pickerPlayerIdSet = useMemo(() => new Set(pickerPlayers.map((p) => p.id)), [pickerPlayers]);

  const pickerPlayersById = useMemo(() => {
    const m = new Map();
    pickerPlayers.forEach((p) => {
      m.set(p.id, p);
    });
    return m;
  }, [pickerPlayers]);

  const buildInitialDraft = useCallback((config) => {
    const manual = Array.isArray(config?.manual_candidate_player_ids) ? config.manual_candidate_player_ids : [];
    const effective = Array.isArray(config?.effective_candidate_player_ids) ? config.effective_candidate_player_ids : [];
    const base = manual.length === 4 ? manual : effective.slice(0, 4);
    const out = [null, null, null, null];
    base.forEach((id, idx) => {
      if (idx < 4) out[idx] = toNumberId(id);
    });
    return out;
  }, []);

  const setDraftForConfig = useCallback(
    (config) => {
      const qid = config?.match_prediction_question_id;
      if (!qid) return;
      setDraftsByQuestionId((prev) => ({ ...prev, [qid]: buildInitialDraft(config) }));
    },
    [buildInitialDraft],
  );

  const load = useCallback(
    async (midRaw) => {
      const mid = String(midRaw || '').trim();
      if (!mid) {
        showSnackbar('Enter a match id.', 'warning');
        return;
      }
      if (!/^\d+$/.test(mid) || Number(mid) <= 0) {
        showSnackbar('Match id must be a positive number.', 'warning');
        return;
      }

      setLoading(true);
      try {
        const res = await getPredictionCandidates(mid);
        if (String(res?.statusCode || '') === '10003') {
          showSnackbar('Session expired. Please login again.', 'error');
          setTimeout(() => {
            window.location = '/login';
          }, 1500);
          return;
        }
        if (!isSuccess(res)) {
          showSnackbar(res?.message || 'Failed to load prediction candidates', 'error');
          return;
        }

        const payload = getPayload(res);
        const nextConfigs = Array.isArray(payload.configs) ? payload.configs : [];
        const nextPlayers = Array.isArray(payload.available_players) ? payload.available_players : [];
        const nextDisplay =
          payload.player_display_by_id && typeof payload.player_display_by_id === 'object'
            ? payload.player_display_by_id
            : {};

        setMeta({
          match_id: payload.match_id ?? mid,
          arcade_match_id: payload.arcade_match_id ?? null,
          prediction_enabled: !!payload.prediction_enabled,
        });
        setConfigs(nextConfigs);
        setAvailablePlayers(nextPlayers);
        setPlayerDisplayById(nextDisplay);
        setSearchParams({ match_id: String(mid) });

        const nextDrafts = {};
        nextConfigs.forEach((cfg) => {
          const qid = cfg?.match_prediction_question_id;
          if (qid) nextDrafts[qid] = buildInitialDraft(cfg);
        });
        setDraftsByQuestionId(nextDrafts);
      } finally {
        setLoading(false);
      }
    },
    [buildInitialDraft, setSearchParams, showSnackbar],
  );

  useEffect(() => {
    if (initialMid) load(initialMid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateDraftSlot = (qid, idx, value) => {
    const pid = toNumberId(value);
    setDraftsByQuestionId((prev) => {
      const curr = Array.isArray(prev[qid]) ? [...prev[qid]] : [null, null, null, null];
      curr[idx] = pid;
      return { ...prev, [qid]: curr };
    });
  };

  const copyEffectiveToDraft = (config) => {
    const qid = config?.match_prediction_question_id;
    if (!qid) return;
    const effective = Array.isArray(config?.effective_candidate_player_ids) ? config.effective_candidate_player_ids : [];
    const next = [null, null, null, null];
    effective.slice(0, 4).forEach((id, i) => {
      next[i] = toNumberId(id);
    });
    setDraftsByQuestionId((prev) => ({ ...prev, [qid]: next }));
  };

  const resetDraft = (config) => {
    const qid = config?.match_prediction_question_id;
    if (!qid) return;
    setDraftsByQuestionId((prev) => ({ ...prev, [qid]: buildInitialDraft(config) }));
  };

  const saveOverride = async (config) => {
    const qid = config?.match_prediction_question_id;
    const matchId = meta?.match_id ?? matchIdInput;
    if (!qid || !matchId) return;

    const draft = (draftsByQuestionId[qid] || []).map((x) => toNumberId(x));
    if (draft.length !== 4 || draft.some((x) => x == null)) {
      showSnackbar('Select exactly 4 players before saving.', 'warning');
      return;
    }
    if (new Set(draft).size !== 4) {
      showSnackbar('Candidates must be 4 distinct player ids.', 'warning');
      return;
    }
    if (draft.some((pid) => !pickerPlayerIdSet.has(pid))) {
      showSnackbar('Each selected player must be part of available squad players.', 'warning');
      return;
    }

    setSavingQuestionId(qid);
    try {
      const res = await patchPredictionCandidates({
        match_id: Number(matchId),
        match_prediction_question_id: qid,
        manual_candidate_player_ids: draft,
      });
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return;
      }
      if (!isSuccess(res)) {
        showSnackbar(res?.message || 'Could not save override', 'error');
        return;
      }

      const data = getPayload(res);
      if (data?.config) {
        setConfigs((prev) =>
          prev.map((c) => (c.match_prediction_question_id === qid ? { ...c, ...data.config } : c)),
        );
        setDraftForConfig(data.config);
      }
      if (data?.player_display_by_id && typeof data.player_display_by_id === 'object') {
        setPlayerDisplayById((prev) => ({ ...prev, ...data.player_display_by_id }));
      }
      showSnackbar('Manual candidate override saved.', 'success');
    } finally {
      setSavingQuestionId(null);
    }
  };

  const clearOverride = async (config) => {
    const qid = config?.match_prediction_question_id;
    const matchId = meta?.match_id ?? matchIdInput;
    if (!qid || !matchId) return;

    setSavingQuestionId(qid);
    try {
      const res = await patchPredictionCandidates({
        match_id: Number(matchId),
        match_prediction_question_id: qid,
        manual_candidate_player_ids: null,
      });
      if (String(res?.statusCode || '') === '10003') {
        showSnackbar('Session expired. Please login again.', 'error');
        setTimeout(() => {
          window.location = '/login';
        }, 1500);
        return;
      }
      if (!isSuccess(res)) {
        showSnackbar(res?.message || 'Could not clear override', 'error');
        return;
      }

      const data = getPayload(res);
      if (data?.config) {
        setConfigs((prev) =>
          prev.map((c) => (c.match_prediction_question_id === qid ? { ...c, ...data.config } : c)),
        );
        setDraftForConfig(data.config);
      } else {
        await load(matchId);
      }
      if (data?.player_display_by_id && typeof data.player_display_by_id === 'object') {
        setPlayerDisplayById((prev) => ({ ...prev, ...data.player_display_by_id }));
      }
      showSnackbar('Manual override cleared. Auto candidates are active.', 'success');
    } finally {
      setSavingQuestionId(null);
    }
  };

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <h3>Arcade Prediction candidates</h3>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <Paper sx={{ p: 2, mb: 2 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Load candidate configs by <code>matches.match_id</code>. For player-based questions, set a manual list of
              exactly 4 distinct player ids in rank order. Team question (<code>Match Winner</code>) is read-only.
              This endpoint edits active questions only.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
              <TextField
                label="Match ID"
                size="small"
                value={matchIdInput}
                onChange={(e) => setMatchIdInput(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Button variant="contained" disabled={loading} onClick={() => load(matchIdInput)}>
                {loading ? <CircularProgress size={20} color="inherit" /> : 'Load'}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to={`/admin/arcade/prediction-questions${matchIdInput ? `?match_id=${encodeURIComponent(matchIdInput)}` : ''}`}
              >
                Manage Active Questions
              </Button>
            </Box>
          </Paper>

          {meta ? (
            <Paper sx={{ p: 2, mb: 2 }}>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip size="small" label={`Match: ${meta.match_id}`} />
                {meta.arcade_match_id ? <Chip size="small" label={`Arcade match: ${meta.arcade_match_id}`} /> : null}
                <Chip
                  size="small"
                  label={`Prediction: ${meta.prediction_enabled ? 'enabled' : 'disabled'}`}
                  color={meta.prediction_enabled ? 'success' : 'default'}
                />
                <Chip size="small" label={`Available players: ${pickerPlayers.length}`} />
              </Box>
            </Paper>
          ) : null}

          {meta && configs.length === 0 ? (
            <Alert severity="info">
              No active prediction question configs found for this match. Configure active questions first, then return
              here for candidate overrides.
            </Alert>
          ) : null}

          {configs.map((config) => {
            const qid = config?.match_prediction_question_id;
            const isTeam = !!config?.is_team_prediction;
            const draft = draftsByQuestionId[qid] || [null, null, null, null];
            const manual =
              Array.isArray(config?.manual_candidate_player_ids) && config.manual_candidate_player_ids.length === 4;
            const effective = Array.isArray(config?.effective_candidate_player_ids)
              ? config.effective_candidate_player_ids
              : [];
            const saving = savingQuestionId === qid;
            const manualList = Array.isArray(config?.manual_candidate_player_ids) ? config.manual_candidate_player_ids : [];
            const sameAsSaved =
              draft.length === 4 &&
              draft.every((x) => x != null) &&
              manualList.length === 4 &&
              draft.every((x, i) => Number(x) === Number(manualList[i]));
            const hasDraftDuplicates = new Set(draft.filter((x) => x != null)).size !== draft.filter((x) => x != null).length;

            return (
              <Paper key={qid} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                  <Typography variant="h6">{config?.title || 'Untitled question'}</Typography>
                  {config?.tag ? <Chip size="small" label={config.tag} /> : null}
                  <Chip size="small" color={isTeam ? 'info' : 'default'} label={isTeam ? 'Team question' : 'Player question'} />
                  {manual ? <Chip size="small" color="warning" label="Manual override active" /> : null}
                </Box>

                {isTeam ? (
                  <Typography variant="body2" color="text.secondary">
                    Team candidates in use: {effective.join(', ') || 'None'}
                  </Typography>
                ) : (
                  <>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Effective candidates:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      {effective.length ? (
                        effective.map((pid, idx) => (
                          <Chip
                            key={`${qid}-effective-${idx}-${pid}`}
                            size="small"
                            label={`${idx + 1}. ${getCandidateName(pid, playerDisplayById, pickerPlayersById)} [${pid}]`}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          None
                        </Typography>
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      Manual override:
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 1.5 }}>
                      {manualList.length === 4 ? (
                        manualList.map((pid, idx) => (
                          <Chip
                            key={`${qid}-manual-${idx}-${pid}`}
                            size="small"
                            color="warning"
                            variant="outlined"
                            label={`${idx + 1}. ${getCandidateName(pid, playerDisplayById, pickerPlayersById)} [${pid}]`}
                          />
                        ))
                      ) : (
                        <Typography variant="caption" color="text.secondary">
                          Not set (auto in use)
                        </Typography>
                      )}
                    </Box>
                    {pickerPlayers.length === 0 ? (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        No available squad players returned for this match. Reload the match and verify squad sync.
                      </Alert>
                    ) : null}
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 1.5, mb: 2 }}>
                      {[0, 1, 2, 3].map((idx) => (
                        <FormControl fullWidth size="small" key={`${qid}-${idx}`}>
                          <InputLabel>{`Candidate ${idx + 1}`}</InputLabel>
                          <Select
                            value={draft[idx] ?? ''}
                            label={`Candidate ${idx + 1}`}
                            onChange={(e) => updateDraftSlot(qid, idx, e.target.value)}
                          >
                            <MenuItem value="">
                              <em>Select player</em>
                            </MenuItem>
                            {pickerPlayers.map((p) => (
                              <MenuItem key={p.id} value={p.id}>
                                {playerLabel(p, playerDisplayById)}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ))}
                    </Box>
                    {hasDraftDuplicates ? (
                      <Alert severity="warning" sx={{ mb: 1.5 }}>
                        Draft contains duplicate player ids. Choose 4 distinct players.
                      </Alert>
                    ) : null}
                    <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                      <Button
                        variant="contained"
                        onClick={() => saveOverride(config)}
                        disabled={saving || pickerPlayers.length === 0 || sameAsSaved}
                      >
                        {saving ? <CircularProgress size={18} color="inherit" /> : 'Save manual override'}
                      </Button>
                      <Button variant="outlined" onClick={() => clearOverride(config)} disabled={saving || !manual}>
                        Clear override (use auto)
                      </Button>
                      <Button variant="text" onClick={() => copyEffectiveToDraft(config)} disabled={saving || effective.length === 0}>
                        Use effective as draft
                      </Button>
                      <Button variant="text" onClick={() => resetDraft(config)} disabled={saving}>
                        Reset draft
                      </Button>
                    </Box>
                  </>
                )}
              </Paper>
            );
          })}
        </div>
      </div>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Component>
  );
}

export default PredictionCandidates;
